import FieldStrategy from '../FieldStrategy';
import { generateFieldName } from '@/views/fusion/investment/intelligence-create/tools';
import { checkboxFilterNone, transformSelectedTimeToTimeSeries, transformTreeKey } from '@/tools/form';
import {
  CAMPAIGN_MODULE_KEY,
  CREATIVE_MODULE_KEY,
  CREATIVE_BASE_FORM_KEY,
  CREATIVE_CONTENT_FORM_KEY,
  TARGETING_MODULE_KEY,
  MATERIAL_MODULE_KEY,
  TITLE_MODULE_KEY,
  SITE_MODULE_KEY,
} from '@/views/fusion/investment/intelligence-create/strategy/constants';
import { OPTIONS_TYPE, CHANNEL_INTERFACE_TYPE, TIME_PERIOD_LENGTH } from '@/enums';
import calculator from '@/utils/calculator';

/**
 * B站策略类
 */
export default class BilibiliFieldStrategy extends FieldStrategy {
  /**
   * 获取差异化模块字段
   * @returns {Object}
   */
  getDifferentiatedFields() {
    return {
      campaign_config: {
        config: {
          delivery_form: {
            config: {
              delivery_platform_type: {
                value: '4',
                backup_value: '4',
              },
              delivery_content_id: {
                type: '',
                is_single_advertiser_assign: true,
                is_open: false,
                value: {
                  type_selection: [
                    { label: 'IOS应用', value: '4' },
                    { label: '微信小游戏', value: '2' },
                  ],
                  result_map: {},
                },
                isPreview: false,
                rules: {
                  required: true,
                  trigger: ['change'],
                  validator: (_, value, callback) => {
                    if (Object.keys(value.result_map).length) {
                      callback();
                    } else {
                      callback(new Error('请选择投放内容'));
                    }
                  },
                },
              },
              support_auto: {
                label: '投放方式',
                type: 'radio-group',
                isButton: true,
                options: [
                  { label: '手动投放', value: '0' },
                  { label: '自动投放', value: '1' },
                ],
                value: '0',
              },
            },
          },
          site_form: {
            config: {
              site_set_type: {
                props: {
                  disabled: true,
                },
                value: 1,
              },
            },
          },
          schedule_form: {
            config: {
              time_period: {
                isHour: true,
                value: { schedule_time_content: new Array(TIME_PERIOD_LENGTH.HOUR).fill(1).join('') },
              },
            },
          },
          budget_and_bid_form: {
            config: {
              daily_budget_type: {
                props: {
                  disabled: true,
                },
                value: 'BUDGET_MODE_DAY',
              },
              daily_budget: {
                hidden: false,
              },
              price_type: {
                value: '999',
              },
              price: {
                hidden: true,
              },
              price_strategy: {
                hidden: false,
                value: '0',
              },
              speed_mode: {
                label: '投放类型',
                type: 'radio-group',
                isButton: true,
                options: [],
                value: '1',
              },
              fusion__optimize_goal: {
                label: '优化目标',
                type: 'select',
                hidden: false,
                props: {
                  clearable: false,
                },
                options: [],
                value: '',
                backup_value: '',
                rules: { required: true, message: '请选择优化目标', trigger: ['change'] },
              },
              deep_convert_type: {
                hidden: true,
              },
              dual_bid_two_stage_optimization: {
                label: '深度优化方式',
                type: 'radio-group',
                hidden: true,
                isButton: true,
                options: [],
                value: '0',
              },
              cpa_bid: {
                label: '优化目标出价/ROI出价',
                type: 'input-number',
                hidden: true,
                props: {
                  step: 1,
                  precision: 4,
                  min: 0.0001,
                  max: 999999999.00,
                },
                value: undefined,
                rules: { required: true, message: '请输入优化目标出价/ROI出价', trigger: ['change'] },
              },
              deep_convert_price: {
                label: '深度优化出价/ROI出价',
                hidden: true,
                props: {
                  step: 1,
                  precision: 4,
                  min: 0.0001,
                  max: 999999999.00,
                },
                formItemTips: {
                  content: '',
                },
                rules: { required: true, message: '请输入深度优化出价/ROI出价', trigger: ['change'] },
              },
            },
          },
        },
      },
      creative_config: {
        config: {
          base_form: {
            config: {
              unit_names: {
                label: '单元名称',
                is_open: false,
                isPreview: false,
                // 临时分配
                assignSelection: {},
                // 格式 - { [advertiser_id]: [{}, {}] }
                value: {},
              },
            },
          },
          content_form: {
            config: {
              delivery_mode: {
                label: '投放模式',
                type: 'radio-group',
                isButton: true,
                options: [
                  { label: '组件化创意', value: '1', disabled: true },
                  { label: '自定义创意', value: '0' },
                ],
                value: '0',
              },
              creative_template_id: {
                label: '创意形式',
                type: 'radio-group',
                isBox: true,
                options: [
                  { label: '视频', value: 4 },
                  { label: '图片', value: 1 },
                ],
                value: 4,
              },
              brands: {
                label: '品牌形象跳转',
                is_open: false,
                isPreview: false,
                value: {
                  type_selection: [
                    { label: '品牌形象', value: 'brands' },
                  ],
                  result_map: {},
                },
                rules: {
                  required: true,
                  trigger: ['change'],
                  validator: (_, value, callback) => {
                    if (Object.keys(value.result_map).length) {
                      callback();
                    } else {
                      callback(new Error('请选择品牌形象跳转'));
                    }
                  },
                },
              },
              story_components: {
                label: 'Story转化组件',
                hidden: false,
                is_open: false,
                isPreview: false,
                value: {
                  type_selection: [
                    { label: '通用基础卡', value: '2' },
                  ],
                  result_map: {},
                },
              },
            },
          },
          creative_components: {
            config: {
              business_category: {
                label: '创意分类',
                type: 'cascader',
                options: [],
                value: [],
              },
              labels: {
                label: '创意标签',
                type: 'form-tags',
                props: {
                  multiple: false,
                  minlength: 1,
                  maxlength: 10,
                  tagsNum: 10,
                },
                value: [],
              },
              description: {
                label: '素材描述',
                type: 'input',
                props: {
                  showWordLimit: true,
                  maxlength: 10,
                },
                value: '',
                rules: { required: true, message: '请输入素材描述', trigger: ['change'] },
              },
              button_id_selection: {
                label: '选择按钮',
                type: 'radio-group',
                isButton: true,
                options: [
                  { label: '有按钮', value: OPTIONS_TYPE.CUSTOM, disabled: false },
                  { label: '无按钮', value: OPTIONS_TYPE.UNLIMITED },
                ],
                value: OPTIONS_TYPE.UNLIMITED,
              },
              button_id: {
                label: ' ',
                type: 'select',
                hidden: true,
                options_map: {},
                options: [],
                value: '1',
                rules: { required: true, message: '请选择按钮文案', trigger: ['change'] },
              },
            },
          },
        },
      },
      targeting_config: {
        config: {
          targeting_form: {
            config: {
              geo_location: {
                children: {
                  residence_geo_locations: {
                    props: {
                      checkStrictly: false,
                    },
                  },
                  current_geo_locations: {
                    props: {
                      checkStrictly: false,
                    },
                  },
                },
              },
              gender: {
                value: '0',
              },
              /** 年龄 */
              ages: {
                value: {},
                children: {
                  /** 年龄选项 */
                  ages_selection: {
                    label: '年龄',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '指定年龄段', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  /** 指定年龄段 */
                  age_custom: {
                    label: ' ',
                    type: 'checkbox-group',
                    hidden: true,
                    isBox: true,
                    options: [],
                    value: [],
                    rules: { required: true, message: '请选择年龄段', trigger: ['change'] },
                  },
                },
              },
              /** 设备 */
              devices: {
                label: '设备',
                type: 'checkbox-group',
                isButton: true,
                value: ['0'],
                backup_value: ['0'],
                options: [],
              },
              /** 设备品牌 */
              device_brand_list: {
                value: {},
                children: {
                  device_brand_selection: {
                    label: '设备品牌',
                    type: 'radio-group',
                    hidden: true,
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  device_brand: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '设备品牌',
                    props: {
                      nodeKey: 'value',
                    },
                    hidden: true,
                    options: [],
                    backup_options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择设备品牌'));
                        }
                      },
                    },
                  },
                },
              },
              /** 操作系统版本 */
              os_version_list: {
                value: {},
                children: {
                  os_version_selection: {
                    label: '操作系统版本',
                    type: 'radio-group',
                    isButton: true,
                    hidden: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  os_version: {
                    label: ' ',
                    type: 'cascader',
                    props: {
                      props: {
                        multiple: true,
                        emitPath: false,
                      },
                    },
                    hidden: true,
                    backup_options: [],
                    options: [],
                    value: [],
                    rules: { required: true, message: '请选择操作系统版本', trigger: ['change'] },
                  },
                },
              },
              /** 兴趣定向 */
              interest: {
                value: {},
                children: {
                  interest_selection: {
                    label: '兴趣定向',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '按行业', value: 'business_interest_list' },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                    labelTips: '注意：投放方式为自动投放时不支持使用兴趣定向配置',
                  },
                  business_interest_list: {
                    label: ' ',
                    type: 'cascader',
                    props: {
                      props: {
                        multiple: true,
                        emitPath: false,
                      },
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: { required: true, message: '请选择行业兴趣定向', trigger: ['change'] },
                  },
                  custom_interest_selection: {
                    label: '自定义兴趣定向',
                    type: 'checkbox-group',
                    hidden: true,
                    isButton: true,
                    props: {
                      min: 1,
                    },
                    options: [
                      { label: '按APP', value: 'app_category' },
                      { label: '按视频关键词', value: 'arc_tag_interest' },
                      { label: '按视频分区', value: 'video_partition_category' },
                    ],
                    value: ['app_category', 'arc_tag_interest', 'video_partition_category'],
                    labelTips: '注意：投放方式为自动投放时不支持使用自定义兴趣定向配置',
                  },
                  app_category: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: 'APP兴趣定向',
                    props: {
                      nodeKey: 'value',
                      checkStrictly: false,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择APP兴趣定向'));
                        }
                      },
                    },
                  },
                  arc_tag_interest: {
                    value: {},
                    children: {
                      tag_fuzzy_type: {
                        label: '视频关键词',
                        type: 'radio-group',
                        hidden: true,
                        isButton: true,
                        options: [],
                        value: '1',
                      },
                      tags: {
                        label: ' ',
                        type: 'form-tags',
                        hidden: true,
                        props: {
                          multiple: false,
                          tagsNum: 10,
                        },
                        value: [],
                        rules: { required: true, message: '请添加视频关键词', trigger: ['change'] },
                      },
                    },
                  },
                  video_partition_category: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '视频分区兴趣定向',
                    props: {
                      nodeKey: 'value',
                      checkStrictly: false,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择视频分区兴趣定向'));
                        }
                      },
                    },
                  },
                },
              },
              /** 粉丝关系 */
              fans: {
                value: {},
                children: {
                  fans_selection: {
                    label: '粉丝关系',
                    type: 'checkbox-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '指定up主', value: 'include_their_fans_mid_list' },
                      { label: '排除up主', value: 'exclude_their_fans_mid_list' },
                    ],
                    value: [OPTIONS_TYPE.UNLIMITED],
                    labelTips: '注意：投放方式为自动投放时不支持使用粉丝关系定向配置',
                  },
                  include_their_fans_mid_list: {
                    label: '指定up主',
                    type: 'form-tags',
                    hidden: true,
                    props: {
                      placeholder: '输入up主mid按回车添加，批量添加多个用、分隔',
                      multiple: true,
                      tagsNum: 10,
                    },
                    value: [],
                    rules: { required: true, message: '请添加指定up主', trigger: ['change'] },
                  },
                  exclude_their_fans_mid_list: {
                    label: '排除up主',
                    type: 'form-tags',
                    hidden: true,
                    props: {
                      placeholder: '输入up主mid按回车添加，批量添加多个用、分隔',
                      multiple: true,
                      tagsNum: 10,
                    },
                    value: [],
                    rules: { required: true, message: '请添加排除up主', trigger: ['change'] },
                  },
                },
              },
              /** 网络 */
              network_list: {
                label: '网络',
                type: 'checkbox-group',
                isButton: true,
                value: ['0'],
                options: [],
                labelTips: '注意：投放方式为自动投放时不支持使用网络定向配置',
              },
              /** 手机价格 */
              device_price_list: {
                label: '手机价格',
                type: 'checkbox-group',
                isButton: true,
                value: ['0'],
                options: [],
                labelTips: '注意：投放方式为自动投放时不支持使用手机价格配置',
              },
              /** 已安装用户 - 无交互，仅作显示 */
              app_install_status: {
                label: '已安装用户',
                type: 'radio-group',
                isButton: true,
                props: {
                  disabled: true,
                },
                options: [
                  { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                ],
                value: OPTIONS_TYPE.UNLIMITED,
              },
              /** 过滤已转化用户 */
              converted_user_filter: {
                label: '过滤已转化用户',
                type: 'radio-group',
                isButton: true,
                options: [],
                value: '0',
              },
              /** 智能扩量 */
              intelligent_mass: {
                value: {},
                children: {
                  intelligent_mass_selection: {
                    label: '智能扩量',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不开启', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '开启', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                    labelTips: '注意：投放方式为自动投放时不支持使用智能扩量定向配置',
                  },
                  intelligent_mass: {
                    label: ' ',
                    type: 'checkbox-group',
                    hidden: true,
                    isButton: true,
                    options: [],
                    value: [],
                    rules: { required: true, message: '请选择智能扩量', trigger: ['change'] },
                  },
                },
              },
            },
          },
        },
      },
      material_config: {
        config: {
          material_form: {
            config: {
              material: {
                props: {
                  type: 'video',
                  tempMaterial: null,
                },
                material_type: 'video',
              },
            },
          },
        },
      },
    };
  }

  /**
   * 添加事件
   * @param {Object} config 模块配置
   * @param {Object} controller 控制器
   */
  addEvent(config, controller) {
    this.config = config;
    super.addEvent(config);
    const {
      campaign_config: {
        config: {
          delivery_form,
          budget_and_bid_form,
        },
      },
      creative_config: {
        config: {
          content_form,
          creative_components,
        },
      },
      targeting_config: {
        config: {
          targeting_form,
        },
      },
      material_config: {
        config: {
          material_form,
        },
      },
    } = config;
    const { delivery_platform_type, delivery_content_id } = delivery_form.config;
    const {
      price_type,
      price,
      price_strategy,
      fusion__optimize_goal,
      deep_convert_type,
      dual_bid_two_stage_optimization,
      cpa_bid,
      deep_convert_price,
    } = budget_and_bid_form.config;
    const {
      creative_template_id,
      story_components,
    } = content_form.config;
    const {
      button_id_selection,
      button_id,
    } = creative_components.config;
    const { material } = material_form.config;

    /**
     * 设置优化目标选项
     * @returns {Promise<void>}
     */
    const setOptimizeGoalData = async() => {
      fusion__optimize_goal.options = await controller.getConvertGoalList({
        bilibili_params: {
          filtering: {
            delivery_platform_type: delivery_platform_type.value,
            price_strategy: price_strategy.value,
          },
        },
      });
      const is_match_optimize_goal = fusion__optimize_goal.options.some(({ value }) => fusion__optimize_goal.value === value);
      if (!is_match_optimize_goal) {
        fusion__optimize_goal.value = '';
        fusion__optimize_goal.backup_value = '';
        deep_convert_type.value = '';
        deep_convert_type.options = [];
      }

      await setDeepConvertTypeData();
    };

    /**
     * 设置深度优化目标选项
     * @returns {Promise<void>}
     */
    const setDeepConvertTypeData = async() => {
      if (fusion__optimize_goal.value) {
        const params = {
          bilibili_params: {
            filtering: {
              convert_goal: fusion__optimize_goal.value,
              delivery_platform_type: delivery_platform_type.value,
              price_strategy: price_strategy.value,
            },
          },
        };
        deep_convert_type.options = await controller.getDeepConvertGoalList(params);
        const is_match = deep_convert_type.options.some(({ value }) => deep_convert_type.value === value);
        !is_match && (deep_convert_type.value = '');
      }
    };

    /**
     * 设置深度优化方式可见性
     * 同时满足以下条件时，可见
     * - 出价方式为【oCPM[999]】
     * - 深度优化目标可见
     * - 深度优化目标有值
     * @returns {void}
     */
    const setDualBidTwoStageOptimizationVisible = () => {
      const is_ocpm = price_type.value === '999';
      const is_deep_convert_type_visible = !deep_convert_type.hidden;
      dual_bid_two_stage_optimization.hidden = !(is_ocpm && is_deep_convert_type_visible && deep_convert_type.value);
    };

    /**
     * 设置深度优化方式选项可选值
     * 同时满足以下条件时，可选【自动优化[1]】
     * - 深度优化目标存在【次日留存[16]】、【应用内付费(首次)[11]】、【表单付费[18]】和【有效线索[13]】之一
     * @returns {void}
     */
    const setDualBidTwoStageOptimizationOptions = () => {
      const is_match = ['16', '11', '18', '13'].includes(deep_convert_type.value);
      dual_bid_two_stage_optimization.options.forEach((opt) => {
        if (opt.value === '1') {
          opt.disabled = !is_match;
        }
      });
      !is_match && (dual_bid_two_stage_optimization.value = '0');
    };

    // 投放平台
    delivery_platform_type.on = {
      change: async() => {
        const is_ios = delivery_platform_type.value === '4';
        const is_wxmg = delivery_platform_type.value === '2'; // 这个是【线索收集】
        const is_wxmg_v2 = delivery_platform_type.value === '66';

        delivery_content_id.value?.type_selection?.splice?.(0);
        delivery_content_id.value?.type_selection?.push?.(...[
          ...(is_ios ? [{ label: 'IOS应用', value: '4' }, { label: '微信小游戏', value: '2' }] : []),
          ...(is_wxmg ? [{ label: '微信小游戏', value: '2' }] : []),
          ...(is_wxmg_v2 ? [{ label: '微信小游戏', value: '66' }] : []),
        ]);

        if (delivery_platform_type.value !== delivery_platform_type.backup_value) {
          delivery_platform_type.backup_value = delivery_platform_type.value;

          delivery_content_id.value.result_map = {};
          await setOptimizeGoalData();
        }

        fusion__optimize_goal.on.change();
      },
    };

    // 投放内容
    delivery_content_id.on = {
      change: () => {},
    };

    // 出价方式
    price_type.on = {
      change: () => {
        const is_ocpm = price_type.value === '999';
        price.hidden = is_ocpm;
        price_strategy.hidden = !is_ocpm;
        fusion__optimize_goal.hidden = !is_ocpm;
        deep_convert_type.hidden = !(is_ocpm && deep_convert_type.options.length);

        setDualBidTwoStageOptimizationVisible();
        dual_bid_two_stage_optimization.on.change();

        // 出价方式为oCPM && 优化目标有值
        cpa_bid.hidden = !(is_ocpm && fusion__optimize_goal.value);
      },
    };

    // 优化目标
    fusion__optimize_goal.on = {
      change: async() => {
        const is_ocpm = price_type.value === '999';
        cpa_bid.hidden = !(is_ocpm && fusion__optimize_goal.value);

        if (fusion__optimize_goal.value !== fusion__optimize_goal.backup_value) {
          fusion__optimize_goal.backup_value = fusion__optimize_goal.value;

          await setDeepConvertTypeData();
        }
        deep_convert_type.hidden = !deep_convert_type.options.length;
        deep_convert_type.on.change();
      },
    };

    // 深度优化目标
    deep_convert_type.on = {
      change: () => {
        setDualBidTwoStageOptimizationVisible();
        setDualBidTwoStageOptimizationOptions();
        dual_bid_two_stage_optimization.on.change();
      },
    };

    // 深度优化方式
    dual_bid_two_stage_optimization.on = {
      change: () => {
        const is_ocpm = price_type.value === '999';
        // 出价方式为oCPM && 深度优化目标有值 && （深度优化方式不可见 || （深度优化方式可见 && 深度优化方式为自定义双出价））
        deep_convert_price.hidden = !(is_ocpm && deep_convert_type.value && (dual_bid_two_stage_optimization.hidden || (!dual_bid_two_stage_optimization.hidden && dual_bid_two_stage_optimization.value === '0')));
      },
    };

    // 创意形式
    creative_template_id.on = {
      change: () => {
        const is_video = creative_template_id.value === 4;
        story_components.hidden = !is_video;
        button_id.options = button_id.options_map[creative_template_id.value];
        const is_match = button_id.options.some(({ value }) => button_id.value === value);
        !is_match && (button_id.value = '');

        const material_type = is_video ? 'video' : 'image';
        if (material.material_type !== material_type) {
          material.material_type = material_type;
          material.value.images = [];
          material.value.videos = [];
          material.value.tab = material_type;
        }
      },
    };

    // 选择按钮
    button_id_selection.on = {
      change: () => {
        const is_custom = button_id_selection.value === OPTIONS_TYPE.CUSTOM;
        button_id.hidden = !is_custom;
      },
    };

    const {
      geo_location: {
        children: {
          geo_type,
          location_type,
        },
      },
      gender,
      audience: {
        children: {
          audience_types,
          include_audiences,
          exclude_audiences,
        },
      },
      ages: {
        children: {
          ages_selection,
          age_custom,
        },
      },
      devices,
      device_brand_list: {
        children: {
          device_brand_selection,
          device_brand,
        },
      },
      os_version_list: {
        children: {
          os_version_selection,
          os_version,
        },
      },
      interest: {
        children: {
          interest_selection,
          business_interest_list,
          custom_interest_selection,
          app_category,
          arc_tag_interest: {
            children: {
              tag_fuzzy_type,
              tags,
            },
          },
          video_partition_category,
        },
      },
      fans: {
        children: {
          fans_selection,
          include_their_fans_mid_list,
          exclude_their_fans_mid_list,
        },
      },
      network_list,
      device_price_list,
      intelligent_mass: {
        children: {
          intelligent_mass_selection,
          intelligent_mass,
        },
      },
    } = targeting_form.config;

    // 地域选项
    geo_type.on = {
      change() {
        const is_location = geo_type.value === 'LOCATION';
        location_type.hidden = !is_location;
        location_type.on.change();

        intelligent_mass.options.forEach(opt => {
          opt.value === '488' && (opt.disabled = !is_location);
        });
        !is_location && (intelligent_mass.value = intelligent_mass.value.filter(val => val !== '488'));
      },
    };

    // 性别
    gender.on = {
      change: () => {
        const is_unlimited = gender.value === '0';

        intelligent_mass.options.forEach(opt => {
          opt.value === '487' && (opt.disabled = is_unlimited);
        });
        is_unlimited && (intelligent_mass.value = intelligent_mass.value.filter(val => val !== '487'));
      },
    };

    // 人群类型
    audience_types.on = {
      change() {
        audience_types.value = checkboxFilterNone(audience_types.value, 'INFINITE');
        const is_include_audience = audience_types.value.includes('INCLUDE');
        const is_exclude_audience = audience_types.value.includes('EXCLUDE');
        include_audiences.hidden = !is_include_audience;
        exclude_audiences.hidden = !is_exclude_audience;

        const is_unlimited = audience_types.value.includes('INFINITE');
        intelligent_mass.options.forEach(opt => {
          opt.value === '490' && (opt.disabled = is_unlimited);
        });
        is_unlimited && (intelligent_mass.value = intelligent_mass.value.filter(val => val !== '490'));
      },
    };

    // 年龄
    ages_selection.on = {
      change: () => {
        const is_custom = ages_selection.value === OPTIONS_TYPE.CUSTOM;
        age_custom.hidden = !is_custom;

        intelligent_mass.options.forEach(opt => {
          opt.value === '486' && (opt.disabled = !is_custom);
        });
        !is_custom && (intelligent_mass.value = intelligent_mass.value.filter(val => val !== '486'));
      },
    };

    // 设备
    devices.on = {
      change: () => {
        devices.value = checkboxFilterNone(devices.value, '0');
        const is_unlimited = devices.value.includes('0');
        const is_has_iphone = devices.value.includes('398');
        const is_has_ipad = devices.value.includes('421');
        const is_has_android = devices.value.includes('399');

        if (JSON.stringify(devices.value) !== JSON.stringify(devices.backup_value)) {
          devices.backup_value = devices.value;
          device_brand.value = [];
          os_version.value = [];
        }

        // 设备品牌
        const device_brand_ios_options = device_brand.backup_options.filter(({ value }) => value === '628');
        const device_brand_android_options = device_brand.backup_options.filter(({ value }) => value !== '628');
        device_brand_selection.hidden = !is_has_android;
        device_brand_selection.on.change();
        device_brand.options = [
          ...((is_has_iphone || is_has_ipad) ? device_brand_ios_options : []),
          ...(is_has_android ? device_brand_android_options : []),
        ];

        // 操作系统版本
        const os_version_iphone_options = os_version.backup_options.filter(({ value }) => value === '398#398');
        const os_version_ipad_options = os_version.backup_options.filter(({ value }) => value === '421#421');
        const os_version_android_options = os_version.backup_options.filter(({ value }) => value === '399#399');
        os_version_selection.hidden = !(!is_unlimited);
        os_version_selection.on.change();
        os_version.options = is_unlimited ? os_version.backup_options : [
          ...(is_has_iphone ? os_version_iphone_options : []),
          ...(is_has_ipad ? os_version_ipad_options : []),
          ...(is_has_android ? os_version_android_options : []),
        ];

        intelligent_mass.options.forEach(opt => {
          opt.value === '635' && (opt.disabled = is_unlimited);
        });
        is_unlimited && (intelligent_mass.value = intelligent_mass.value.filter(val => val !== '635'));
      },
    };

    // 设备品牌
    device_brand_selection.on = {
      change: () => {
        const is_custom = device_brand_selection.value === OPTIONS_TYPE.CUSTOM;
        const is_has_android = devices.value.includes('399');
        device_brand.hidden = !(is_custom && is_has_android);

        intelligent_mass.options.forEach(opt => {
          opt.value === '636' && (opt.disabled = !(is_custom && is_has_android));
        });
        !(is_custom && is_has_android) && (intelligent_mass.value = intelligent_mass.value.filter(val => val !== '636'));
      },
    };

    // 操作系统版本
    os_version_selection.on = {
      change: () => {
        const is_custom = os_version_selection.value === OPTIONS_TYPE.CUSTOM;
        const is_unlimited = devices.value.includes('0');
        os_version.hidden = !(is_custom && !is_unlimited);
      },
    };

    // 兴趣定向
    interest_selection.on = {
      change: () => {
        const is_custom = interest_selection.value === OPTIONS_TYPE.CUSTOM;
        const is_business_interest_list = interest_selection.value === 'business_interest_list';
        business_interest_list.hidden = !is_business_interest_list;
        custom_interest_selection.hidden = !is_custom;
        custom_interest_selection.on.change();
      },
    };

    // 自定义兴趣定向
    custom_interest_selection.on = {
      change: () => {
        const is_custom = interest_selection.value === OPTIONS_TYPE.CUSTOM;
        const is_app_category = custom_interest_selection.value.includes('app_category');
        const is_arc_tag_interest = custom_interest_selection.value.includes('arc_tag_interest');
        const is_video_partition_category = custom_interest_selection.value.includes('video_partition_category');
        app_category.hidden = !(is_app_category && is_custom);
        tag_fuzzy_type.hidden = !(is_arc_tag_interest && is_custom);
        tags.hidden = !(is_arc_tag_interest && is_custom);
        video_partition_category.hidden = !(is_video_partition_category && is_custom);
      },
    };

    // 粉丝关系
    fans_selection.on = {
      change: () => {
        fans_selection.value = checkboxFilterNone(fans_selection.value, OPTIONS_TYPE.UNLIMITED);
        const is_include_their_fans_mid_list = fans_selection.value.includes('include_their_fans_mid_list');
        const is_exclude_their_fans_mid_list = fans_selection.value.includes('exclude_their_fans_mid_list');
        include_their_fans_mid_list.hidden = !is_include_their_fans_mid_list;
        exclude_their_fans_mid_list.hidden = !is_exclude_their_fans_mid_list;
      },
    };

    // 指定up主
    include_their_fans_mid_list.on = {
      change: () => {
        const exclude_value = exclude_their_fans_mid_list.value;
        include_their_fans_mid_list.value = include_their_fans_mid_list.value.filter(include_value => !exclude_value.includes(include_value));
      },
    };

    // 排除up主
    exclude_their_fans_mid_list.on = {
      change: () => {
        const include_value = include_their_fans_mid_list.value;
        exclude_their_fans_mid_list.value = exclude_their_fans_mid_list.value.filter(exclude_value => !include_value.includes(exclude_value));
      },
    };

    // 网络
    network_list.on = {
      change: () => {
        network_list.value = checkboxFilterNone(network_list.value, '0');
      },
    };

    // 手机价格
    device_price_list.on = {
      change: () => {
        device_price_list.value = checkboxFilterNone(device_price_list.value, '0');
      },
    };

    // 智能扩量
    intelligent_mass_selection.on = {
      change: () => {
        const is_custom = intelligent_mass_selection.value === OPTIONS_TYPE.CUSTOM;
        intelligent_mass.hidden = !is_custom;
      },
    };
  }

  /**
   * 获取覆盖字符对象
   * @returns {Object}
   */
  getDifferentiatedOverrides() {
    /** 单元名称 */
    const unit_names = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_BASE_FORM_KEY, 'unit_names']);

    /** 品牌形象跳转 */
    const brands = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_CONTENT_FORM_KEY, 'brands']);

    /** Story转化组件 */
    const story_components = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_CONTENT_FORM_KEY, 'story_components']);

    return {
      [unit_names]: ({ field_config }) => {
        field_config.isPreview = true;
      },

      [brands]: ({ field_config }) => {
        field_config.isPreview = true;
      },

      [story_components]: ({ field_config }) => {
        field_config.isPreview = true;
      },
    };
  }

  /**
   * 设置配置模块的数据源
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   */
  async setFieldDataSources(config, controller) {
    try {
      await super.setFieldDataSources(config, controller);
      const {
        campaign_config: {
          config: {
            delivery_form,
            budget_and_bid_form,
          },
        },
        creative_config: {
          config: {
            creative_components,
          },
        },
      } = config;
      const { delivery_platform_type } = delivery_form.config;
      const {
        price_strategy,
        speed_mode,
        fusion__optimize_goal,
        dual_bid_two_stage_optimization,
      } = budget_and_bid_form.config;
      const { business_category, button_id } = creative_components.config;
      const promises_map = [
        /** 获取投放类型枚举 */
        [
          () => controller.getSpeedModeEnums(),
          (res) => {
            speed_mode.options = res;
          },
        ],
        /** 获取优化目标选项 */
        [
          () => {
            const params = {
              bilibili_params: {
                filtering: {
                  delivery_platform_type: delivery_platform_type.value,
                  price_strategy: price_strategy.value,
                },
              },
            };
            return controller.getConvertGoalList(params);
          },
          (res) => {
            fusion__optimize_goal.options = res;
          },
        ],
        /** 获取深度优化方式枚举 */
        [
          () => controller.getDualBidTwoStageOptimizationEnums(),
          (res) => {
            res.forEach((opt) => {
              if (opt.value === '1') {
                Object.assign(opt, { disabled: true });
              }
            });
            dual_bid_two_stage_optimization.options = res;
          },
        ],
        /** 获取创意分类选项 */
        [
          () => controller.getBusinessCategoryList(),
          (res) => {
            business_category.options = res;
          },
        ],
        /** 获取选择按钮枚举 */
        [
          () => controller.getButtonIdEnumsMap(),
          (res) => {
            button_id.options_map = res;
            button_id.options = button_id.options_map[4];
          },
        ],
      ];
      const values = await Promise.all(promises_map.map(([promise]) => promise()));
      promises_map.forEach(([_, handler], index) => handler(values[index]));
    } catch (error) {
      console.error(error);
      throw new Error('BilibiliFieldStrategy: setFieldDataSources fail');
    }
  }

  /**
   * 设置定向模块的数据源
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   */
  async setTargetingDataSources(config, controller) {
    await super.setTargetingDataSources(config, controller);
    const {
      gender,
      ages: {
        children: {
          age_custom,
        },
      },
      devices,
      device_brand_list: {
        children: {
          device_brand,
        },
      },
      os_version_list: {
        children: {
          os_version,
        },
      },
      interest: {
        children: {
          business_interest_list,
          app_category,
          arc_tag_interest: {
            children: {
              tag_fuzzy_type,
            },
          },
          video_partition_category,
        },
      },
      network_list,
      device_price_list,
      converted_user_filter,
      intelligent_mass: {
        children: {
          intelligent_mass,
        },
      },
    } = config.targeting_config.config.targeting_form.config;

    // 定向字典
    const targeting_dictionary = await controller.getTargetingDictionary(['age', 'device', 'device_brand', 'device_price', 'os', 'network_type', 'converted_user_filter', 'app_category', 'business_interest', 'tag_fuzzy_type', 'video_partition_category', 'intelligent_mass']);

    // 性别
    gender.options = targeting_dictionary?.gender || [];
    // 年龄
    age_custom.options = targeting_dictionary?.age || [];
    // 设备
    devices.options = targeting_dictionary?.device || [];
    // 设备品牌
    device_brand.options = targeting_dictionary?.device_brand || [];
    device_brand.backup_options = targeting_dictionary?.device_brand || [];
    // 操作系统版本
    const os_options = (targeting_dictionary?.os || []).map(({ label, value, children }) => {
      return {
        label,
        value: `${value}#${value}`,
        children: children.map(child => {
          return {
            label: child.label,
            value: `${value}#${child.value}`,
          };
        }),
      };
    });
    os_version.options = os_options;
    os_version.backup_options = os_options;
    // 行业兴趣定向
    business_interest_list.options = transformTreeKey({ options: targeting_dictionary.business_interest });
    // APP兴趣定向
    app_category.options = targeting_dictionary?.app_category || [];
    // 视频关键词
    tag_fuzzy_type.options = targeting_dictionary?.tag_fuzzy_type || [];
    // 视频分区兴趣定向
    video_partition_category.options = targeting_dictionary?.video_partition_category || [];
    // 网络
    network_list.options = targeting_dictionary?.network_type || [];
    // 手机价格
    device_price_list.options = targeting_dictionary?.device_price || [];
    // 过滤已转化用户
    converted_user_filter.options = targeting_dictionary?.converted_user_filter || [];
    // 智能扩量
    intelligent_mass.options = (targeting_dictionary?.intelligent_mass || []).map(item => ({ ...item, disabled: true }));
  }

  /**
   * 模块字段排序
   * @param {Object} fields
   * @returns {Object}
   */
  async setFieldsSort(fields) {
    const { campaign_config } = fields;
    const {
      daily_budget_type,
      daily_budget,
      price_type,
      price_strategy,
      price,
      convert_id,
      fusion__optimize_goal,
      deep_convert_type,
      dual_bid_two_stage_optimization,
      speed_mode,
      cpa_bid,
      deep_convert_price,
      ...rest
    } = campaign_config.config.budget_and_bid_form.config;
    campaign_config.config.budget_and_bid_form.config = {
      daily_budget_type,
      daily_budget,
      price_type,
      price_strategy,
      price,
      convert_id,
      fusion__optimize_goal,
      deep_convert_type,
      dual_bid_two_stage_optimization,
      speed_mode,
      cpa_bid,
      deep_convert_price,
      ...rest,
    };
    return Promise.resolve(fields);
  }

  /**
   * 转换差异化广告预览参数
   * @param {Object} data 数据
   * @param {Object} data.form_assign_data 提交参数
   */
  transformDifferentiatedCampaignPreviewPayload({ form_assign_data }) {
    const {
      campaign_config: {
        delivery_form: {
          support_auto,
        },
        schedule_form: {
          time_period_type,
          schedule_time,
          time_period,
        },
        budget_and_bid_form: {
          speed_mode,
          fusion__optimize_goal,
          deep_convert_type,
          dual_bid_two_stage_optimization,
          cpa_bid,
        },
      },
      creative_config: {
        base_form: {
          unit_names,
        },
      },
    } = form_assign_data;

    // 单元名称
    const unit_names_value = Object.entries(unit_names).filter(([_, list]) => list.some(({ unit_name }) => unit_name)).map(([advertiser_id, list]) => ({ advertiser_id, unit_names: list.filter(({ unit_name }) => unit_name) }));

    return {
      /** 投放时间类型 */
      time_period_type,
      /** 投放时间 - 全天 */
      ...(time_period_type === 'TIME_PERIOD_ALL' && { time_period: new Array(TIME_PERIOD_LENGTH.HOUR).fill(1).join('') }),
      /** 投放时间 - 指定时间 */
      ...(time_period_type === 'TIME_PERIOD_DAY_SPECIFIC' && { time_period: transformSelectedTimeToTimeSeries(schedule_time.start, schedule_time.end, true) }),
      /** 投放时间 - 指定多个时间 */
      ...(time_period_type === 'TIME_PERIOD_HOUR_SPECIFIC' && { time_period: time_period.schedule_time_content }),
      /** 投放方式 */
      support_auto,
      /** 投放类型 */
      speed_mode,
      /** 优化目标 */
      ...(fusion__optimize_goal && { convert_goal: fusion__optimize_goal }),
      /** 深度优化目标 */
      ...(deep_convert_type && { deep_convert_type }),
      /** 深度优化方式 */
      ...(dual_bid_two_stage_optimization && { dual_bid_two_stage_optimization }),
      /** 优化目标/ROI系数出价 */
      ...(cpa_bid && { cpa_bid: calculator.times(cpa_bid, 100) }),
      /** 单元名称 */
      ...(unit_names_value?.length && { unit_names: unit_names_value }),
    };
  }

  /**
   * 转换差异化创意预览参数
   * @param {Object} data 数据
   * @param {Object} data.form_assign_data 提交参数
   * @returns {Object}
   */
  transformDifferentiatedCreativePreviewPayload({ form_assign_data }) {
    const {
      creative_config: {
        content_form: {
          delivery_mode,
          creative_template_id,
          brands,
          story_components,
        } = {},
        creative_components: {
          business_category,
          labels,
          description,
          button_id_selection,
          button_id,
        } = {},
      },
    } = form_assign_data;

    const brands_value = Object.entries(brands?.result_map || {}).map(([advertiser_id, config]) => ({ advertiser_id, id: config.value.split('#')[0], name: config.value.split('#')[1] }));
    const story_components_value = Object.entries(story_components?.result_map || {}).map(([advertiser_id, config]) => ({ advertiser_id, id: config.value.split('#')[0], type: config.type }));

    return {
      /** 投放模式 */
      delivery_mode,
      /** 创意形式 */
      creative_template_id,
      /** 品牌形象 */
      brands: brands_value,
      /** Story转化组件 */
      ...(story_components_value.length && { story_components: story_components_value }),
      /** 创意分类 */
      ...(business_category?.length && { business_category: { first_category_id: business_category[0], second_category_id: business_category[1] }}),
      /** 创意标签 */
      ...(labels?.length && { labels }),
      /** 素材描述 */
      description,
      /** 选择按钮  */
      ...(button_id_selection === OPTIONS_TYPE.CUSTOM && { button_id }),
    };
  }

  /**
   * 获取创建定向包差异化接口参数
   * @param {Object} target_data 定向数据
   * @returns {Object}
   */
  getDifferentiatedCreateTargetParams(target_data) {
    const {
      gender,
      ages: {
        ages_selection,
        age_custom,
      },
      devices,
      device_brand_list: {
        device_brand_selection,
        device_brand,
      } = {},
      os_version_list: {
        os_version_selection,
        os_version,
      },
      interest: {
        interest_selection,
        business_interest_list,
        custom_interest_selection,
        app_category,
        arc_tag_interest: {
          tag_fuzzy_type,
          tags,
        } = {},
        video_partition_category,
      },
      fans: {
        fans_selection,
        include_their_fans_mid_list,
        exclude_their_fans_mid_list,
      },
      network_list,
      device_price_list,
      converted_user_filter,
      intelligent_mass: {
        intelligent_mass_selection,
        intelligent_mass,
      },
    } = target_data;

    // 操作系统版本
    const os_version_map = {};
    (os_version || []).forEach(os => {
      const key = os.split('#')[0];
      const value = os.split('#')[1];
      if (os_version_map[key]) {
        os_version_map[key].push(value);
      } else {
        Object.assign(os_version_map, {
          [key]: [value],
        });
      }
    });

    const target_params = {
      gender,
      [`${CHANNEL_INTERFACE_TYPE.BILIBILI}_params`]: {
        // 年龄
        ...(ages_selection === OPTIONS_TYPE.CUSTOM && { ages: age_custom }),
        // 设备信息
        ...(!devices.includes('0') && {
          os_list: devices.map(device => {
            return {
              // 设备
              os_target: device,
              // 设备品牌
              ...((device_brand_selection === OPTIONS_TYPE.CUSTOM && device === '399') && { device_brand_list: device_brand }),
              // 操作系统版本
              ...((os_version_selection === OPTIONS_TYPE.CUSTOM && os_version_map[device]?.length) && { os_version_list: os_version_map[device] }),
            };
          }),
        }),
        // 行业兴趣定向
        ...(interest_selection === 'business_interest_list' && { business_interest_list }),
        // APP兴趣定向
        ...((interest_selection === OPTIONS_TYPE.CUSTOM && custom_interest_selection.includes('app_category')) && { app_category }),
        // 视频关键词
        ...((interest_selection === OPTIONS_TYPE.CUSTOM && custom_interest_selection.includes('arc_tag_interest')) && { arc_tag_interest: { tag_fuzzy_type, tags }}),
        // 视频分区兴趣定向
        ...((interest_selection === OPTIONS_TYPE.CUSTOM && custom_interest_selection.includes('video_partition_category')) && { video_partition_category }),
        // 粉丝关系
        ...(!fans_selection.includes(OPTIONS_TYPE.UNLIMITED) && {
          fans: {
            ...(fans_selection.includes('include_their_fans_mid_list') && { include_their_fans_mid_list }),
            ...(fans_selection.includes('exclude_their_fans_mid_list') && { exclude_their_fans_mid_list }),
          },
        }),
        // 网络
        network_list,
        // 手机价格
        device_price_list,
        // 过滤已转化用户
        converted_user_filter,
        // 智能扩量
        ...(intelligent_mass_selection === OPTIONS_TYPE.CUSTOM && { intelligent_mass }),
      },
    };

    return target_params;
  }

  /**
   * 获取定向包差异化详情表单数据
   * @param {Object} target_params 定向详情数据
   * @returns {Object}
   */
  getDifferentiatedTargetDetailFormData(target_params) {
    const {
      gender,
      [`${CHANNEL_INTERFACE_TYPE.BILIBILI}_params`]: {
        ages,
        os_list,
        business_interest_list,
        app_category,
        arc_tag_interest,
        video_partition_category,
        fans,
        network_list,
        device_price_list,
        converted_user_filter,
        intelligent_mass,
      } = {},
    } = target_params;

    // 设备
    const devices = [];
    // 设备品牌
    const device_brand = [];
    // 操作系统版本
    const os_version = [];
    (os_list || []).forEach(({ os_target, os_version_list = [], device_brand_list = [] }) => {
      devices.push(os_target);
      (os_target === '399' && device_brand_list.length) && device_brand.push(...device_brand_list);
      os_version_list.forEach(val => {
        os_version.push(`${os_target}#${val}`);
      });
    });

    const form_data = {
      // 性别
      gender,
      // 年龄
      ...(ages && {
        ages: {
          ages_selection: OPTIONS_TYPE.CUSTOM,
          age_custom: ages,
        },
      }),
      // 设备
      ...(devices.length && { devices }),
      // 设备品牌
      ...(device_brand.length && {
        device_brand_list: {
          device_brand_selection: OPTIONS_TYPE.CUSTOM,
          device_brand,
        },
      }),
      // 操作系统版本
      ...(os_version.length && {
        os_version_list: {
          os_version_selection: OPTIONS_TYPE.CUSTOM,
          os_version,
        },
      }),
      // 兴趣定向
      ...((business_interest_list || app_category || arc_tag_interest || video_partition_category) && {
        interest: {
          interest_selection: business_interest_list ? 'business_interest_list' : OPTIONS_TYPE.CUSTOM,
          ...(business_interest_list && { business_interest_list }),
          ...((app_category || arc_tag_interest || video_partition_category) && {
            custom_interest_selection: [
              ...(app_category ? ['app_category'] : []),
              ...(arc_tag_interest ? ['arc_tag_interest'] : []),
              ...(video_partition_category ? ['video_partition_category'] : []),
            ],
          }),
          ...(app_category && { app_category }),
          ...(arc_tag_interest && { arc_tag_interest: {
            tag_fuzzy_type: arc_tag_interest.tag_fuzzy_type,
            tags: arc_tag_interest.tags,
          }}),
          ...(video_partition_category && { video_partition_category }),
        },
      }),
      // 粉丝关系
      ...(fans && {
        fans: {
          fans_selection: [
            ...(fans.include_their_fans_mid_list ? ['include_their_fans_mid_list'] : []),
            ...(fans.exclude_their_fans_mid_list ? ['exclude_their_fans_mid_list'] : []),
          ],
          ...(fans.include_their_fans_mid_list && { include_their_fans_mid_list: fans.include_their_fans_mid_list }),
          ...(fans.exclude_their_fans_mid_list && { exclude_their_fans_mid_list: fans.exclude_their_fans_mid_list }),
        },
      }),
      // 网络
      network_list,
      // 手机价格
      device_price_list,
      // 过滤已转化用户
      converted_user_filter,
      // 智能扩量
      ...(intelligent_mass && {
        intelligent_mass: {
          intelligent_mass_selection: OPTIONS_TYPE.CUSTOM,
          intelligent_mass,
        },
      }),
    };

    return form_data;
  }

  /**
   * 获取定向数据转预览数据关联表
   * @returns {Object}
   */
  getTargetPreviewAssociatedMap() {
    return {
      ages_selection: {
        associated_keys: ['age_custom'],
      },
      device_brand_selection: {
        associated_keys: ['device_brand'],
      },
      os_version_selection: {
        associated_keys: ['os_version'],
      },
      interest_selection: {
        type: 'options',
        associated_keys: ['business_interest_list', 'custom_interest_selection'],
        associated_options_map: {
          business_interest_list: 'business_interest_list',
        },
      },
      custom_interest_selection: {
        type: 'options',
        associated_keys: ['app_category', 'tag_fuzzy_type', 'tags', 'video_partition_category'],
        associated_options_map: {
          app_category: 'app_category',
          tag_fuzzy_type: 'arc_tag_interest',
          tags: 'arc_tag_interest',
          video_partition_category: 'video_partition_category',
        },
      },
      fans_selection: {
        type: 'options',
        associated_keys: ['include_their_fans_mid_list', 'exclude_their_fans_mid_list'],
        associated_options_map: {
          include_their_fans_mid_list: 'include_their_fans_mid_list',
          exclude_their_fans_mid_list: 'exclude_their_fans_mid_list',
        },
      },
      intelligent_mass_selection: {
        associated_keys: ['intelligent_mass'],
      },
    };
  }

  /**
   * 获取素材属性
   * @param {Object} material_config 素材表单配置
   * @returns {Object}
   */
  getMaterialProps(material_config) {
    const { material_type } = material_config?.config?.material_form?.config?.material;
    return {
      ...(material_type && { type: material_type }),
    };
  }

  /**
   * 设置源数据表单
   * @param {Object} source 源数据
   * @param {Object} target 目标数据
   * @returns {void}
   */
  setSourceFormFieldsData(source, target) {
    const {
      material_config: {
        config: {
          material_form: {
            config: {
              material,
            },
          },
        },
      },
    } = source;

    const {
      material_config: {
        config: {
          material_form: {
            config: {
              material: target_material,
            },
          },
        },
      },
    } = target;

    Object.assign(material, {
      value: target_material.value,
      material_type: target_material.material_type,
    });
  }

  /**
   * 获取差异化模板配置表
   * @returns {Object}
   */
  getDifferentiatedTemplateMap() {
    return {
      campaign_config: {
        delivery_form: ['support_auto'],
      },
      creative_config: {
        content_form: ['delivery_mode', 'creative_template_id'],
      },
    };
  }

  /**
   * 是否显示预览按钮
   * @param {Object} form_assign_data 分配数据
   * @param {Object} form_fields 源数据
   * @param {Object} payload 配置参数
   * @returns {boolean}
   */
  isShowPreviewBtn(form_assign_data, form_fields, payload) {
    const { is_add_creative } = payload;
    const material = form_assign_data[MATERIAL_MODULE_KEY];
    const is_has_material = material?.images?.length > 0 || material?.videos?.length > 0;
    const {
      campaign_config: {
        config: {
          delivery_form: {
            config: {
              delivery_platform_type,
            },
          },
        },
      },
    } = form_fields;

    /**
     * ios应用
     * - 落地页为非必填
     */
    const is_ios = delivery_platform_type.value === '4';

    if (is_add_creative) {
      // 添加创意
      const assign_keys = [
        TITLE_MODULE_KEY,
        ...(is_ios ? [] : [SITE_MODULE_KEY]),
      ];
      return assign_keys.every(key => form_assign_data[key]?.length > 0) && is_has_material;
    } else {
      if (is_ios) {
        const basic_keys = [CAMPAIGN_MODULE_KEY, CREATIVE_MODULE_KEY];
        const assign_keys = [TARGETING_MODULE_KEY, TITLE_MODULE_KEY];

        const valid_list = [
          basic_keys.every(key => !!form_assign_data[key]),
          assign_keys.every(key => form_assign_data[key]?.length > 0),
        ];
        return valid_list.every(v => v);
      } else {
        return super.isShowPreviewBtn(form_assign_data, form_fields);
      }
    }
  }
}
